import React, { useState, useEffect } from 'react';
import api from '../services/api';

export default function Dashboard() {
  const [psriData, setPsriData] = useState([]);
  const [ultgSummary, setUltgSummary] = useState(null);
  const [peringatan, setPeringatan] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState('');

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const res = await api.get('/dashboard');
      const responseData = res.data.data || {};
      setPsriData(responseData.gardu_induk || []);
      setUltgSummary(responseData.ultg || null);
      setLastUpdate(new Date().toLocaleString('id-ID'));
    } catch (err) {
      console.error('Error:', err);
      setPsriData([]);
      setUltgSummary(null);
    }
    try {
      const resPeringatan = await api.get('/monitoring/peringatan-dini');
      setPeringatan(resPeringatan.data.data.items || []);
    } catch (err) {
      console.error('Error peringatan dini:', err);
      setPeringatan([]);
    }
    setIsLoading(false);
  };

  const getGIStatus = (gi) => {
    if (gi.apar_sudah_inspeksi === null || gi.apar_sudah_inspeksi === undefined) return 'belum';
    const nilai = parseFloat(gi.nilai_psri || 0);
    if (nilai >= 80) return 'siap';
    if (nilai >= 60) return 'sebagian';
    return 'tidak_siap';
  };

  const inspeksiCount = psriData.filter(g => g.apar_sudah_inspeksi !== null && g.apar_sudah_inspeksi !== undefined).length;
  const siapCount = psriData.filter(g => getGIStatus(g) === 'siap').length;
  const tidakSiapCount = psriData.filter(g => getGIStatus(g) === 'tidak_siap').length;
  const totalGI = psriData.length;

  const avgPsri = ultgSummary ? parseFloat(ultgSummary.nilai_psri || 0) : 0;

  const getGaugeStatus = (nilai) => {
    if (nilai >= 80) return { label: 'Siap', color: '#00843D' };
    if (nilai >= 60) return { label: 'Siap Sebagian', color: '#B87700' };
    return { label: 'Tidak Siap', color: '#D9362B' };
  };
  const gaugeStatus = getGaugeStatus(avgPsri);
  const needleAngle = -90 + (Math.max(0, Math.min(100, avgPsri)) / 100) * 180;

  if (isLoading) return (
    <div className="flex items-center justify-center h-screen">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
        <p className="text-gray-500">Memuat data...</p>
      </div>
    </div>
  );

  return (
    <div className="p-4 h-screen flex flex-col overflow-hidden">
      <div className="flex justify-between items-center mb-3 bg-green-50 rounded-xl px-5 py-3 border border-green-100">
        <div>
          <h1 className="text-xl font-bold text-gray-800">Beranda</h1>
          <p className="text-gray-500 text-xs mt-0.5">
            Halo, <span className="font-semibold text-gray-700">Administrator PSRI</span>
            {' '}&middot; <span className="text-green-700 font-semibold">&#9679; LIVE</span>
            {' '}&middot; Update: {lastUpdate}
          </p>
        </div>
        <button onClick={loadData} className="bg-green-700 text-white px-4 py-2 rounded-lg hover:bg-green-600 transition-colors text-sm font-semibold shadow-sm">
          &#8635; Refresh
        </button>
      </div>

      <div className="flex-1 overflow-y-auto space-y-3">

        <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-3">
          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 flex flex-col items-center justify-center">
            <p className="text-[10px] font-mono tracking-widest text-gray-400 self-start mb-1">RATA-RATA PSRI</p>
            <svg viewBox="0 0 260 150" width="190" className="-mb-2">
              <path d="M 20 140 A 110 110 0 0 1 88 34" stroke="#D9362B" strokeWidth="16" fill="none" strokeLinecap="round" opacity="0.85" />
              <path d="M 92 31 A 110 110 0 0 1 168 31" stroke="#E0A526" strokeWidth="16" fill="none" strokeLinecap="round" opacity="0.85" />
              <path d="M 172 34 A 110 110 0 0 1 240 140" stroke="#00A651" strokeWidth="16" fill="none" strokeLinecap="round" opacity="0.85" />
              <g transform={`rotate(${needleAngle} 130 140)`}>
                <line x1="130" y1="140" x2="130" y2="38" stroke="#132318" strokeWidth="3.5" strokeLinecap="round" />
              </g>
              <circle cx="130" cy="140" r="8" fill="#132318" />
              <circle cx="130" cy="140" r="3.5" fill="#fff" />
            </svg>
            <p className="text-3xl font-bold font-mono -mt-5" style={{ color: gaugeStatus.color }}>{avgPsri.toFixed(1)}</p>
            <p className="text-[10px] text-gray-400">dari skala 0&ndash;100 &middot; Status: <b style={{ color: gaugeStatus.color }}>{gaugeStatus.label}</b></p>
          </div>

          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 flex flex-col justify-center gap-2">
            <div className="flex items-center justify-between">
              <p className="text-[10px] font-mono tracking-widest text-gray-400">&#9888; PERINGATAN DINI</p>
              {peringatan.length > 0 && (
                <span className="text-[10px] font-mono font-bold px-2.5 py-1 rounded-full bg-yellow-50 text-yellow-700 border border-yellow-200">
                  {peringatan.length} ITEM PERLU PERHATIAN
                </span>
              )}
            </div>
            {peringatan.length === 0 ? (
              <div className="text-center py-3">
                <p className="text-gray-400 text-sm">Tidak ada peringatan aktif</p>
                <p className="text-gray-300 text-xs mt-0.5">Semua APAR &amp; P3K dalam kondisi baik</p>
              </div>
            ) : (
              <div className="flex flex-col gap-1.5">
                {peringatan.slice(0, 5).map((item, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs">
                    <span
                      className="text-[9px] font-mono font-bold px-1.5 py-0.5 rounded min-w-[64px] text-center"
                      style={{
                        backgroundColor: item.urgensi === 'kritis' ? '#FDEBEA' : '#FFF4DE',
                        color: item.urgensi === 'kritis' ? '#D9362B' : '#B87700',
                      }}
                    >
                      {item.urgensi === 'kritis' ? 'KRITIS' : 'PERHATIAN'}
                    </span>
                    <span className="flex-1 font-medium text-gray-700 truncate">{item.kode} &middot; {item.gi_nama}</span>
                    <span className={`font-mono text-[10px] whitespace-nowrap ${item.urgensi === 'kritis' ? 'text-red-600 font-semibold' : 'text-gray-400'}`}>
                      {item.keterangan}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 flex divide-x divide-gray-100">
          <div className="flex-1 p-4">
            <p className="text-[10px] font-mono tracking-widest text-blue-500">INSPEKSI</p>
            <p className="text-2xl font-bold font-mono text-blue-600 mt-1">{inspeksiCount} / {totalGI}</p>
            <p className="text-[10px] text-gray-400 mt-0.5">GI sudah diinspeksi bulan ini</p>
          </div>
          <div className="flex-1 p-4">
            <p className="text-[10px] font-mono tracking-widest text-green-600">SIAP</p>
            <p className="text-2xl font-bold font-mono text-green-700 mt-1">{siapCount}</p>
            <p className="text-[10px] text-gray-400 mt-0.5">&#8805; 80% &middot; dari {totalGI} GI</p>
          </div>
          <div className="flex-1 p-4">
            <p className="text-[10px] font-mono tracking-widest text-red-500">TIDAK SIAP</p>
            <p className="text-2xl font-bold font-mono text-red-600 mt-1">{tidakSiapCount}</p>
            <p className="text-[10px] text-gray-400 mt-0.5">&lt; 60% (gagal standar) &middot; dari {totalGI} GI</p>
          </div>
        </div>

        <div>
          <h2 className="text-xs font-bold text-green-800 tracking-wide mb-2">DETAIL PER GARDU INDUK</h2>
          <div className="flex flex-wrap gap-2.5">
            {psriData.map((gi) => {
              const status = getGIStatus(gi);
              if (status === 'belum') {
                return (
                  <div key={gi.id} className="flex-1 min-w-[190px] bg-gray-50 rounded-xl p-3.5 border border-gray-200">
                    <div className="flex justify-between items-center gap-2 mb-2">
                      <span className="text-sm font-semibold text-gray-700 truncate">{gi.nama}</span>
                      <span className="text-[9px] font-mono font-bold px-2 py-1 rounded-full bg-gray-200 text-gray-500 whitespace-nowrap">&ndash; BELUM INSPEKSI</span>
                    </div>
                    <p className="text-2xl font-bold font-mono text-gray-300">&mdash;<span className="text-xs text-gray-400">/100</span></p>
                    <p className="text-[10px] text-gray-400 italic mt-1">Belum ada data inspeksi bulan ini</p>
                  </div>
                );
              }
              const statusColor = status === 'siap' ? 'text-green-700 bg-green-50' : status === 'sebagian' ? 'text-yellow-700 bg-yellow-50' : 'text-red-700 bg-red-50';
              const scoreColor = status === 'siap' ? 'text-green-700' : status === 'sebagian' ? 'text-yellow-600' : 'text-red-600';
              return (
                <div key={gi.id} className="flex-1 min-w-[190px] bg-white rounded-xl p-3.5 shadow-sm border border-gray-100">
                  <div className="flex justify-between items-center gap-2 mb-2">
                    <span className="text-sm font-semibold text-gray-800 truncate">{gi.nama}</span>
                    <span className={`text-[9px] font-mono font-bold px-2 py-1 rounded-full whitespace-nowrap ${statusColor}`}>
                      {status === 'siap' ? '✓ SIAP' : status === 'sebagian' ? 'SEBAGIAN' : 'TIDAK SIAP'}
                    </span>
                  </div>
                  <p className={`text-2xl font-bold font-mono ${scoreColor}`}>{parseFloat(gi.nilai_psri).toFixed(1)}<span className="text-xs text-gray-400">/100</span></p>
                  <div className="space-y-1.5 mt-2">
                    {[['APAR', gi.nilai_apar], ['APD', gi.nilai_apd], ['P3K', gi.nilai_p3k]].map(([label, val]) => (
                      <div key={label} className="grid grid-cols-[32px_1fr_28px] items-center gap-1.5">
                        <span className="text-[9px] font-mono text-gray-400">{label}</span>
                        <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                          <div className="h-full bg-green-600 rounded-full" style={{ width: `${Math.min(parseFloat(val), 100)}%` }}></div>
                        </div>
                        <span className="text-[9px] font-mono text-gray-400 text-right">{parseFloat(val).toFixed(0)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

      </div>
    </div>
  );
}
